import type {
  MachineAttachmentItemDto,
  MachineQuotationItemDto,
  QuotationDto,
  QuotationPriceOnlyDto
} from '@api/machines'
import {
  ATTACHMENT_SUB_PATH,
  EQUIPMENT_SUB_PATH,
  formatPriceAndCurrency,
  QuoteItemStockStatus
} from '@common/utils'
import { useDisclosure } from '@components/hooks'
import {
  MolsonBox,
  MolsonMoreActions,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Banner, BannerType } from '../banner/Banner'
import { DeleteDialog } from '../delete-dialog/DeleteDialog'
import { useQuoteEquipmentStatus, useQuoteEquipmentUtils } from '../hooks'
import { ReplaceQuotationItemDialog } from '../replace-quotation-item/ReplaceQuotationItemDialog'
import { StockItemChip } from '../stock-item-chip/StockItemChip'

type AttachmentCardProps = {
  attachment: MachineAttachmentItemDto
  deleteAttachmentWithinMachine?: ({
    machineItemId,
    attachmentItemId
  }: {
    machineItemId: string
    attachmentItemId: string
  }) => Promise<void>
  isNonEditableQuote?: boolean
  removeTopMargin?: boolean
  quotation?: QuotationDto
  machine: MachineQuotationItemDto
  quoteNewPrices?: QuotationPriceOnlyDto
  saveMachine?: (machineItemId?: string) => Promise<void>
  translationPrefix: string
  deletePending?: boolean
}

export const AttachmentCard = ({
  attachment,
  removeTopMargin = false,
  deleteAttachmentWithinMachine,
  isNonEditableQuote = false,
  machine,
  quotation,
  quoteNewPrices,
  saveMachine,
  translationPrefix,
  deletePending
}: AttachmentCardProps) => {
  const { t } = useTranslation('', {
    keyPrefix: translationPrefix
  })
  const theme = molsonUseTheme()
  const navigate = useNavigate()
  const { equipmentId: equipmentIdParam, quoteId, specId } = useParams()

  const [
    isDeleteItemModalOpen,
    { close: closeDeleteItemModal, open: openDeleteItemModal }
  ] = useDisclosure()

  const [
    isReplaceQuotationItemModalOpen,
    {
      close: closeReplaceQuotationItemDialog,
      open: openReplaceQuotationItemDialog
    }
  ] = useDisclosure()

  const { brand, machineType, modelName, manufactureYear, price, stockType } =
    attachment

  const equipmentId = machine.quotationItemId
  const isInEditEq = !!equipmentIdParam

  const newPrice = quoteNewPrices?.machineAttachmentItems?.find(
    newPriceAttachment =>
      newPriceAttachment.quotationItemId === attachment.quotationItemId
  )?.price

  const {
    itemIsOutOfStock,
    isEqArchived,
    isEqBrandArchived,
    itemUsesLifeCycleCosting,
    itemUsesNetCapitalizedCosting,
    itemLifeCycleCostChange,
    soldPendingDeliveryWithPriceChanges,
    problematicItemsCount
  } = useQuoteEquipmentStatus(
    attachment,
    quotation,
    quoteNewPrices as QuotationPriceOnlyDto,
    true
  )

  const { bannerText, bannerType, cardBorder, bannerButtonText } =
    useQuoteEquipmentUtils(
      attachment,
      quotation,
      quoteNewPrices as QuotationPriceOnlyDto,
      true
    )

  const showFindReplacementButton =
    itemIsOutOfStock &&
    attachment.stockInfo?.stockStatus !==
      QuoteItemStockStatus.SOLD_PENDING_DELIVERY

  const textColor =
    isEqArchived || isEqBrandArchived
      ? theme.palette.secondary[500]
      : theme.palette.secondary[900]

  const displayBanner =
    (bannerType || soldPendingDeliveryWithPriceChanges) &&
    (isEqArchived ||
      isEqBrandArchived ||
      problematicItemsCount > 0 ||
      itemIsOutOfStock ||
      itemUsesLifeCycleCosting ||
      itemUsesNetCapitalizedCosting ||
      itemLifeCycleCostChange.changed)

  const onEdit = () => {
    let path = ''

    if (isInEditEq) {
      path = `${ATTACHMENT_SUB_PATH}/${attachment.quotationItemId}?edit=true`
    } else {
      path = `${EQUIPMENT_SUB_PATH}/${machine.quotationItemId}/${ATTACHMENT_SUB_PATH}/${attachment.quotationItemId}?edit=true`
    }

    navigate(path)
  }

  const deleteAttAndSaveMachine = async () => {
    if (
      !(
        attachment?.quotationItemId &&
        (equipmentId || machine.quotationItemId) &&
        (quoteId || specId)
      )
    ) {
      return
    }

    await deleteAttachmentWithinMachine?.({
      attachmentItemId: attachment.quotationItemId,
      machineItemId: machine.quotationItemId ?? ''
    })

    if (quoteId && !isInEditEq) {
      saveMachine?.(machine.quotationItemId ?? undefined)
    }
  }

  const handleDeleteAttachment = () => {
    deleteAttAndSaveMachine()
  }

  return (
    <>
      <MolsonBox
        sx={{
          border: cardBorder,
          borderRadius: 2,
          display: 'flex',
          flexDirection: ' column',
          justifyContent: 'space-between',
          marginTop: removeTopMargin ? 0 : 2.5,
          padding: 3
        }}
        bgcolor={theme.palette.secondary[100]}
        boxShadowType="default"
      >
        <MolsonBox
          alignItems="center"
          display="flex"
          gap={2}
          justifyContent="space-between"
          width="100%"
        >
          <MolsonBox
            display="flex"
            flex={1}
            flexDirection="column"
            justifyContent="center"
          >
            {itemIsOutOfStock ? (
              <StockItemChip
                stockStatus={
                  attachment.stockInfo?.stockStatus ?? attachment.stockType
                }
              />
            ) : (
              <MolsonTypography
                variant="body2"
                sx={{
                  color: textColor
                }}
              >
                {stockType} {machineType}
              </MolsonTypography>
            )}

            <MolsonTypography
              variant="h3"
              sx={{
                color: textColor
              }}
            >
              {brand} {modelName} {manufactureYear}
            </MolsonTypography>
          </MolsonBox>

          <MolsonBox alignItems="center" display="flex" gap={2}>
            <MolsonTypography variant="h4">
              {formatPriceAndCurrency(newPrice ?? price)}
            </MolsonTypography>

            <MolsonMoreActions
              isMoreIconDisabled={isEqArchived || isEqBrandArchived}
              actions={[
                {
                  iconVariant: 'EDIT_PENCIL',
                  label: t('cardAction', {
                    action: isNonEditableQuote ? 'View' : 'Edit',
                    rest: 'attachment spec'
                  }),
                  onAction: onEdit
                },
                {
                  disabled: isNonEditableQuote,
                  iconVariant: 'TRASH_CONTAINED',
                  iconColor: 'error',
                  label: t('cardAction', {
                    action: 'Delete',
                    rest: 'attachment'
                  }),
                  onAction: () => openDeleteItemModal()
                }
              ]}
            />
          </MolsonBox>
        </MolsonBox>

        {displayBanner ? (
          <Banner
            isItemArchived={isEqArchived || isEqBrandArchived}
            handleRemoveArchivedItem={handleDeleteAttachment}
            customSx={{
              backgroundColor:
                bannerType === BannerType.Error
                  ? theme.palette.primary.errorBannerBg
                  : theme.palette.secondary[50],
              border: `1px solid ${
                bannerType === BannerType.Error
                  ? theme.palette.primary.errorBannerBg
                  : theme.palette.secondary.warning
              }`,
              py: 1
            }}
            secondaryText={bannerText}
            stockIssue={
              showFindReplacementButton
                ? {
                    findReplacementBtnText: t('findReplacement'),
                    handleFindReplacement: openReplaceQuotationItemDialog,
                    handleRemove: handleDeleteAttachment,
                    removeBtnText: t('remove')
                  }
                : null
            }
            soldPendingDeliveryWithPriceChanges={
              soldPendingDeliveryWithPriceChanges
            }
            buttonText={bannerButtonText}
            handleBannerAction={onEdit}
            reviseOrUpdate={true}
            bannerType={bannerType}
            isNonEditableQuote={isNonEditableQuote}
          />
        ) : null}
      </MolsonBox>

      {isReplaceQuotationItemModalOpen && quotation && (
        <ReplaceQuotationItemDialog
          quote={quotation}
          closeDialog={() => closeReplaceQuotationItemDialog()}
          isDialogOpen={isReplaceQuotationItemModalOpen}
          quotationItem={attachment}
        />
      )}

      <DeleteDialog
        onDelete={deleteAttAndSaveMachine}
        onClose={() => closeDeleteItemModal()}
        open={isDeleteItemModalOpen}
        subTitle={t('deleteRecord')}
        title={t('deleteAttachmentFromMachine')}
        deletePending={deletePending}
      />
    </>
  )
}
